/**
 * Stage Charges modal — the buyer-facing breakdown of a package's installment (v3_34).
 *
 * The BV associate splits the installment amount into charge lines (Materials,
 * Labor, Transportation, Wastage, GST, …). The sum MUST equal the installment —
 * Save is disabled until it reconciles exactly. GST is an ordinary TAXES_GST line.
 * Buyers see these category totals in the app; the ID's material prices are never
 * exposed. A package with no lines is valid (buyer just sees the total).
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Check } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../../components/Buttons";
import { saveStageCharges } from "../../services/interiorExecution";

export const CHARGE_TYPES = [
  ["MATERIALS", "Materials"],
  ["LABOR", "Labor"],
  ["TRANSPORTATION", "Transportation"],
  ["LOADING_UNLOADING", "Loading & Unloading"],
  ["WASTAGE", "Wastage"],
  ["INSTALLATION", "Installation"],
  ["EQUIPMENT_RENTAL", "Equipment Rental"],
  ["DEBRIS_REMOVAL", "Debris Removal"],
  ["SITE_PROTECTION", "Site Protection"],
  ["DESIGN_SUPERVISION_FEE", "Design Supervision Fee"],
  ["CONTINGENCY", "Contingency"],
  ["MOVE_IN_SERVICE", "Move-in Service"],
  ["TAXES_GST", "GST"],
  ["OTHER", "Other"],
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const StageChargesModal = ({ stage, onClose, onSaved }) => {
  const [rows, setRows] = useState(
    (stage.charges || []).length
      ? stage.charges.map((c) => ({ chargeType: c.chargeType, description: c.description || "", amount: String(c.amount) }))
      : [{ chargeType: "MATERIALS", description: "", amount: "" }]
  );
  const [saving, setSaving] = useState(false);

  // The breakdown total IS the installment amount (set on save).
  const total = useMemo(
    () => round2(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)),
    [rows]
  );
  const hasLines = rows.length > 0 && rows.every((r) => parseFloat(r.amount) > 0);

  const update = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((p) => [...p, { chargeType: "MATERIALS", description: "", amount: "" }]);
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!hasLines) return toast.error("Every line needs an amount greater than 0");
    if (rows.some((r) => r.chargeType === "OTHER" && !r.description.trim()))
      return toast.error("'Other' lines need a description");
    setSaving(true);
    try {
      const items = rows.map((r) => ({
        chargeType: r.chargeType,
        description: r.description.trim() || null,
        amount: parseFloat(r.amount),
      }));
      await saveStageCharges(stage.idExecutionPlanStageId, items);
      toast.success(`Breakdown saved — installment set to ${fmtINR(total)}`);
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save the breakdown");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Installment Breakdown — {stage.stageLabel || stage.stageKey}</h3>
            <p className="text-xs text-slate-500">The line-item total becomes this package's installment amount (shown to the buyer). GST is a line here.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-2 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={r.chargeType}
                onChange={(e) => update(i, { chargeType: e.target.value })}
                className="rounded-lg border border-slate-300 text-sm py-2 px-2 bg-white w-44 focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
              >
                {CHARGE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input
                value={r.description}
                onChange={(e) => update(i, { description: e.target.value })}
                placeholder={r.chargeType === "OTHER" ? "Description (required)" : "Note (optional)"}
                className="flex-1 rounded-lg border border-slate-300 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
              />
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <input
                  type="number" min="0" value={r.amount}
                  onChange={(e) => update(i, { amount: e.target.value })}
                  className="w-28 rounded-lg border border-slate-300 text-sm py-2 pl-6 pr-2 text-right focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
                />
              </div>
              <button onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={addRow} className="inline-flex items-center gap-1.5 text-sm text-flamingo-600 hover:text-flamingo-700 font-medium mt-1">
            <Plus size={14} /> Add line
          </button>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end text-sm">
          <span className="font-semibold inline-flex items-center gap-1.5 text-slate-700">
            <Check size={15} className="text-emerald-500" /> Installment total {fmtINR(total)}
          </span>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
          <OutlineButton className="px-4 py-2 text-sm" onClick={onClose}>Cancel</OutlineButton>
          <PrimaryButton className="px-5 py-2 text-sm" onClick={handleSave} disabled={saving || !hasLines}>
            {saving ? "Saving…" : "Save Breakdown"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default StageChargesModal;
